import { useEffect, useMemo, useState } from "react";
import { Dropdown } from "primereact/dropdown";
import { Slider } from "primereact/slider";

import Comparativo from "./Comparativo";
import MediaMovel from "./MediaMovel";
import SuavizacaoExponencial from "./SuavizacaoExponencial";
import TendenciaSazonalidade from "./TendenciaSazonalidade";
import VisaoGeral from "./VisaoGeral";
import {
  buildForecastLabels,
  buildMonthlySeries,
  calcularPrevisoes,
  calcularResumoHistorico,
} from "./previsaoSaidaTecidoUtils";

const tabs = [
  ["overview", "Visão geral"],
  ["moving", "Média móvel"],
  ["exponential", "Suavização exponencial"],
  ["seasonal", "Tendência e sazonalidade"],
  ["comparison", "Comparativo"],
];

const PrevisaoSaidaTecido = ({ items, articleOptions, startDate, endDate }) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [forecastHorizon, setForecastHorizon] = useState(3);
  const [movingWindow, setMovingWindow] = useState(3);
  const [smoothingAlpha, setSmoothingAlpha] = useState(0.3);
  const [seriesType, setSeriesType] = useState("general");
  const seriesOptions = useMemo(() => [
    { label: "Geral", value: "general" },
    ...articleOptions,
  ], [articleOptions]);

  useEffect(() => {
    if (!seriesOptions.some((option) => String(option.value) === String(seriesType))) {
      setSeriesType("general");
    }
  }, [seriesOptions, seriesType]);
  const selectedReportItems = useMemo(() => seriesType === "general"
    ? items
    : items.filter((item) => String(item.artigo) === String(seriesType)), [items, seriesType]);
  const monthlySeries = useMemo(
    () => buildMonthlySeries(selectedReportItems, startDate, endDate),
    [endDate, selectedReportItems, startDate],
  );
  const previsoes = useMemo(
    () => calcularPrevisoes(monthlySeries, forecastHorizon, movingWindow, smoothingAlpha),
    [forecastHorizon, monthlySeries, movingWindow, smoothingAlpha],
  );
  const forecastLabels = useMemo(
    () => buildForecastLabels(monthlySeries, forecastHorizon),
    [forecastHorizon, monthlySeries],
  );
  const resumoHistorico = useMemo(
    () => calcularResumoHistorico(monthlySeries),
    [monthlySeries],
  );
  const hasMinimumHistory = monthlySeries.length >= 6;
  const hasSeasonalHistory = monthlySeries.length >= 24;

  if (!items.length) return null;

  return (
    <section className="p-mt-5">
      <h2>Análise e previsão da saída</h2>

      <div className="p-formgrid p-grid p-ai-end p-mb-4">
        <div className="p-field p-col-12 p-md-4">
          <label>Série analisada</label>
          <Dropdown
            filter
            onChange={(event) => setSeriesType(event.value)}
            options={seriesOptions}
            optionLabel="label"
            optionValue="value"
            value={seriesType}
          />
        </div>
        <div className="p-field p-col-12 p-md-4">
          <label>Horizonte: {forecastHorizon} {forecastHorizon === 1 ? "mês" : "meses"}</label>
          <Slider
            min={1}
            max={6}
            value={forecastHorizon}
            onChange={(event) => setForecastHorizon(event.value)}
          />
        </div>
        <div className="p-field p-col-12 p-md-4">
          <span>{monthlySeries.length} meses no histórico</span>
        </div>
      </div>

      <div className="p-d-flex p-flex-wrap p-mb-4" style={{ gap: 8 }}>
        {tabs.map(([value, label]) => (
          <button
            className={`p-button p-component ${activeTab === value ? "" : "p-button-outlined"}`}
            key={value}
            onClick={() => setActiveTab(value)}
            type="button"
          >
            <span className="p-button-label">{label}</span>
          </button>
        ))}
      </div>

      {!hasMinimumHistory ? (
        <div className="p-message p-message-warn p-p-3">
          São necessários pelo menos 6 meses para gerar uma previsão minimamente útil.
        </div>
      ) : null}

      {activeTab === "overview" && resumoHistorico ? (
        <VisaoGeral
          futureLabels={forecastLabels}
          overview={resumoHistorico}
          series={monthlySeries}
        />
      ) : null}

      {activeTab === "moving" ? (
        <MediaMovel
          available={hasMinimumHistory}
          forecasts={previsoes}
          futureLabels={forecastLabels}
          periods={movingWindow}
          series={monthlySeries}
          setPeriods={setMovingWindow}
        />
      ) : null}

      {activeTab === "exponential" ? (
        <SuavizacaoExponencial
          alpha={smoothingAlpha}
          available={hasMinimumHistory}
          forecast={previsoes.exponential}
          futureLabels={forecastLabels}
          series={monthlySeries}
          setAlpha={setSmoothingAlpha}
        />
      ) : null}

      {activeTab === "seasonal" ? (
        <TendenciaSazonalidade
          available={hasSeasonalHistory}
          forecast={previsoes.seasonal}
          futureLabels={forecastLabels}
          series={monthlySeries}
        />
      ) : null}

      {activeTab === "comparison" ? (
        <Comparativo
          available={hasMinimumHistory}
          forecasts={previsoes}
          futureLabels={forecastLabels}
          series={monthlySeries}
        />
      ) : null}
    </section>
  );
};

export default PrevisaoSaidaTecido;
